import type { TodoDto } from "../../../services/vorgangService";
import type { VerlaufEintrag } from "./verlauf";

/**
 * Der Zusatz, unter dem der bisherige Vorgangs-Verlauf bis zum 13.09.2026 beim
 * ersten To-do aufgefangen wurde: „‹Betreff› - Vorarbeiten".
 *
 * NUR NOCH WIEDERERKENNUNG, keine Vergabe mehr. Seit der Zeichnung vom
 * 13.09.2026 („der Vorgang wird zum ersten To-do") trägt das erste To-do den
 * Vorgangsbetreff selbst; {@link planeTodoAnlage} hängt nichts mehr an.
 * Bestandsdaten werden NICHT umbenannt — das wäre eine stille Datenänderung —,
 * also muss {@link istVorarbeiten} den alten Namen weiterhin kennen, damit
 * solche To-dos in der Sortierung oben bleiben.
 */
export const VORARBEITEN_SUFFIX = " - Vorarbeiten";

/**
 * Trägt das To-do den ALTEN Auffang-Namen (Bestand vor dem 13.09.2026)?
 *
 * Kein Erkennungsmerkmal für neue erste To-dos — die heißen wie ihr Vorgang.
 * Wer wissen will, welches das erste To-do IST, fragt die Sortierung nach dem
 * Alter ({@link sortiereTodosNeuesteZuerst}).
 */
export function istVorarbeiten(todo: Pick<TodoDto, "titel">): boolean {
    return (todo.titel ?? "").endsWith(VORARBEITEN_SUFFIX);
}

/**
 * Steht der Betreff des VORGANGS bereits als Betreff dieses To-dos auf der
 * Seite?
 *
 * Die V4-Ansicht nimmt dem Kopf seit dem 13.09.2026 die Betreffzeile, weil das
 * erste To-do denselben Text trägt. Das gilt aber NUR für To-dos, die
 * {@link planeTodoAnlage} angelegt hat — und das greift ausschließlich im
 * leeren Vorgang. Es bleiben zwei Gegenfälle, in denen das erste To-do den
 * Betreff NICHT trägt:
 *
 *  · Jemand benennt das erste To-do um — das ändert `todo.titel`, nicht
 *    `vorgang.titel`.
 *  · Jemand löscht das erste To-do.
 *
 * In beiden stünde der Vorgangsbetreff sonst nirgends mehr auf der Seite und
 * wäre auch nicht mehr änderbar, während die Vorgangsliste ihn weiter zeigt.
 * Deshalb entscheidet diese Frage, ob der Kopf ihn behält.
 *
 * DER ALT-ZUSATZ „ - Vorarbeiten" ZÄHLT SEIT DEM 13.09.2026 MIT (PO,
 * Optik-Sanierung). Bis dahin galt der Bestand („‹Betreff› - Vorarbeiten",
 * {@link VORARBEITEN_SUFFIX}) bewusst als „trägt ihn nicht", damit der Betreff
 * erreichbar blieb. Gemessen wurde danach das Gegenteil: die Regel griff auf
 * den Testvorgängen des PO praktisch immer, der Kopf behielt seine Betreffzeile
 * und stand mit 205 px statt 80 px über der Seite — mit dem Vorgangsbetreff
 * einmal im Kopf und ein zweites Mal, um „ - Vorarbeiten" ergänzt, am ersten
 * To-do. Zwei fast gleiche Überschriften untereinander sind der größere
 * Schaden. Der Preis ist benannt und angenommen: in diesen Bestands-Vorgängen
 * ist der Vorgangsbetreff in V4 nicht mehr zu ändern — der Text steht am
 * To-do, und umbenannt wird dort. Umbenannt oder gelöscht wird NICHTS: das
 * wäre eine stille Datenänderung (PO-Entscheidung 1 vom 13.09.2026).
 *
 * VERGLICHEN WIRD EXAKT (nur Rand-Leerraum weg) — die Ähnlichkeit muss der
 * Zusatz sein, nicht ein zufälliger Teiltreffer. „Gewerbe prüfen — Nachtrag"
 * trägt den Betreff also weiterhin nicht.
 *
 * Ein LEERER Betreff wird nie getragen: Vorgänge der Arten mit automatischem
 * Betreff (JG/NKG/GAB) haben `titel === vorgangsart`, `betreffOhneArt` liefert
 * dort den leeren String, und `planeTodoAnlage` fällt auf den Namen „Vorgang"
 * zurück. Das Feld im Kopf bleibt dann leer und lädt zum Nachtragen ein —
 * genau dafür ist es da.
 */
export function todoTraegtVorgangsBetreff(
    todoTitel: string | null | undefined,
    vorgangsBetreff: string | null | undefined,
): boolean {
    const betreff = (vorgangsBetreff ?? "").trim();
    if (!betreff) return false;
    const titel = (todoTitel ?? "").trim();
    return titel === betreff || titel === betreff + VORARBEITEN_SUFFIX;
}

/**
 * Was beim Anlegen des ERSTEN To-dos zusätzlich passieren muss.
 *
 * Der Übergang vom flachen Vorgang in To-dos darf den vorhandenen Verlauf
 * nicht unsichtbar machen: sobald es To-dos gibt, zeigen die Ansichten den
 * Vorgangsstrom nicht mehr. Deshalb entsteht vor dem angeforderten To-do ein
 * erstes, das den Vorgang fortsetzt — mit SEINEM Betreff (Zeichnung PO
 * 13.09.2026) — und alles übernimmt, was bislang direkt am Vorgang hing.
 *
 * `erstesTodoTitel === null` heißt: es gibt schon To-dos, es ist nichts zu tun.
 */
export function planeTodoAnlage(_input: { vorgangTitel: string; todos: TodoDto[]; eintraege: VerlaufEintrag[] }): {
    erstesTodoTitel: string | null;
    zuUebernehmendeEintraege: VerlaufEintrag[];
} {
    if (_input.todos.length > 0) {
        return { erstesTodoTitel: null, zuUebernehmendeEintraege: [] };
    }
    // Der Rückfall bleibt: ein To-do ohne Titel wäre in der Liste nicht
    // wiederzufinden.
    const titel = _input.vorgangTitel.trim() || "Vorgang";
    return {
        erstesTodoTitel: titel,
        zuUebernehmendeEintraege: _input.eintraege.filter((e) => e.todoId == null),
    };
}

/**
 * Die `id` des ERSTEN To-dos dieser Liste — des ältesten, bei gleichem
 * Zeitstempel des mit der kleinsten `id`. `null`, wenn kein einziges To-do
 * einen lesbaren `createdAt` trägt.
 *
 * Warum überhaupt: bis zum 13.09.2026 hieß das aufgefangene erste To-do
 * „‹Betreff› - Vorarbeiten", und die Sortierung erkannte es an diesem Zusatz
 * wieder. Seit es schlicht den Vorgangsbetreff trägt, gibt der Name nichts
 * mehr her — das Alter schon. Das erste To-do ist das älteste, per Definition.
 *
 * To-dos OHNE lesbaren Zeitstempel zählen hier NICHT mit: eine fehlende
 * Angabe ist kein früher Zeitpunkt. Zählte sie mit, risse ein einziger
 * kaputter Datensatz das erste To-do von seinem Platz und stünde selbst oben.
 *
 * Der TIE-BREAK über `id` ist Pflicht, kein Beiwerk: die Migration, die
 * `createdAt` nachgezogen hat, gab allen Altzeilen denselben `now()`-Wert.
 * Ohne ihn wäre bei Bestandsdaten beliebig, welches To-do als „erstes" gilt.
 */
function ersteTodoId(todos: TodoDto[]): number | null {
    let treffer: TodoDto | null = null;
    let bestZeit = 0;
    for (const t of todos) {
        const ms = Date.parse(t.createdAt ?? "");
        if (Number.isNaN(ms)) continue;
        if (treffer === null || ms < bestZeit || (ms === bestZeit && t.id < treffer.id)) {
            treffer = t;
            bestZeit = ms;
        }
    }
    return treffer?.id ?? null;
}

/**
 * To-dos neueste zuerst (PO 04.09.2026: „wenn ein neues To-do bei einem Vorgang
 * dazukommt, dann ist das das erste To-do — jetzt gerade als zweites bzw.
 * unten").
 *
 * MIT EINER AUSNAHME: das ERSTE To-do des Vorgangs steht immer ganz oben
 * (PO 05.09.2026: „die Logik neues To-do in einem leeren Vorgang: Vorgang -
 * Vorarbeit und dann neues To-do, von der Reihenfolge nicht andersherum").
 * Es entsteht im selben Handgriff wie das zweite, nur Sekundenbruchteile
 * früher — nach „neueste zuerst" landete es darunter. Inhaltlich ist es aber
 * das ÄLTESTE am Vorgang: es trägt alles, was vor der Aufteilung in To-dos
 * passiert ist. Deshalb ein eigener Rang vor der Zeit, kein Sonderfall in der
 * Zeitrechnung.
 *
 * ERKANNT wird es seit dem 13.09.2026 am ALTER ({@link ersteTodoId}), nicht
 * mehr am Namen: das erste To-do trägt jetzt den Vorgangsbetreff selbst.
 * {@link istVorarbeiten} bleibt als zweiter Weg daneben stehen — für den
 * Bestand, dessen erstes To-do noch „- Vorarbeiten" heißt. Für Altdaten
 * liefern beide Wege dasselbe To-do; der Name ist dort nur der billigere Weg
 * zur selben Antwort.
 *
 * Der TIE-BREAK über `id` ist Pflicht, kein Beiwerk: die Migration, die
 * `createdAt` nachgezogen hat, gab allen Altzeilen denselben `now()`-Wert.
 * Ohne ihn stünden sämtliche Bestands-To-dos in einer beliebigen, bei jedem
 * Laden anderen Reihenfolge.
 *
 * Sortiert wird im FRONTEND: `findOne` teilen sich Alt- und Neuansicht samt
 * weiterer Aufrufer, und der einzige Spec, der die Methode berührt, mockt sie
 * weg — eine Backend-Sortierung wäre dort komplett ungedeckt.
 *
 * Liefert eine KOPIE; `Array.prototype.sort` arbeitete sonst auf dem State.
 */
export function sortiereTodosNeuesteZuerst(todos: TodoDto[]): TodoDto[] {
    const zeit = (t: TodoDto): number => {
        const ms = Date.parse(t.createdAt ?? "");
        return Number.isNaN(ms) ? 0 : ms;
    };
    const erstes = ersteTodoId(todos);
    const rang = (t: TodoDto): number => (t.id === erstes || istVorarbeiten(t) ? 0 : 1);
    return [...todos].sort(
        (a, b) => rang(a) - rang(b) || zeit(b) - zeit(a) || b.id - a.id,
    );
}
